import React, { useEffect, useRef, useState } from 'react';
import { useDriverLocationsStore } from '../../../../store/driverLocationsStore';
import styles from './LiveTrackingBanner.module.css';

// Backend write throttle, matching the Driver web pattern in
// pages/driver/Dashboard/components/DriverLocationMap.tsx.
const PUSH_INTERVAL_MS = 30_000;
// A push older than this is no longer considered "live" for display purposes.
const STALE_AFTER_MS = 90_000;

type GeoState = 'checking' | 'unsupported' | 'denied' | 'error' | 'acquiring' | 'active';

/**
 * Sales Rep half of VP-FIELD-001. POST /api/v1/driver-locations now accepts
 * the Sales Rep role (backend Batch A3), so this banner does the same thing
 * DriverLocationMap.tsx does for drivers: watch the browser's real GPS and
 * push fixes to the backend — then honestly reflect whether that's actually
 * happening (permission state + time since last successful send), instead
 * of the old static "Always On / Broadcasting" markup that was true
 * regardless of whether anything was ever sent.
 */
export const LiveTrackingBanner: React.FC = () => {
  const recordLocation = useDriverLocationsStore((s) => s.record);
  const [geoState, setGeoState] = useState<GeoState>('checking');
  const [lastSentAt, setLastSentAt] = useState<Date | null>(null);
  const [, forceTick] = useState(0);
  const lastPushAttemptAt = useRef<number>(0);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoState('unsupported');
      return;
    }
    setGeoState('acquiring');

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setGeoState('active');
        const now = Date.now();
        if (now - lastPushAttemptAt.current >= PUSH_INTERVAL_MS) {
          lastPushAttemptAt.current = now;
          recordLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy_meters: pos.coords.accuracy,
            heading_degrees: pos.coords.heading ?? undefined,
            speed_kmh: pos.coords.speed != null ? pos.coords.speed * 3.6 : undefined,
            captured_at: new Date(pos.timestamp).toISOString(),
          })
            .then(() => setLastSentAt(new Date()))
            .catch(() => setGeoState('error'));
        }
      },
      (err) => {
        setGeoState(err.code === err.PERMISSION_DENIED ? 'denied' : 'error');
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [recordLocation]);

  // Re-render periodically so "last sent Ns ago" and staleness stay fresh.
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 10_000);
    return () => clearInterval(t);
  }, []);

  const secsSinceSend = lastSentAt ? Math.round((Date.now() - lastSentAt.getTime()) / 1000) : null;
  const isLive = geoState === 'active' && secsSinceSend !== null && secsSinceSend * 1000 < STALE_AFTER_MS;

  let title = 'Live Location Tracking';
  let subtitle = 'Your manager can track your location and route progress in real-time.';

  if (geoState === 'unsupported') {
    title = 'Live Tracking Unavailable';
    subtitle = 'This browser does not support geolocation.';
  } else if (geoState === 'denied') {
    title = 'Live Tracking Off';
    subtitle = 'Location permission was denied — your manager cannot see your position until you allow it.';
  } else if (geoState === 'error') {
    title = 'Live Tracking Interrupted';
    subtitle = 'Your last location update failed to reach the server.';
  } else if (geoState === 'checking' || geoState === 'acquiring') {
    title = 'Acquiring Your Location…';
    subtitle = 'Waiting for a GPS fix before broadcasting starts.';
  } else if (isLive) {
    title = 'Live Location Tracking Active';
    subtitle = `Your manager can track your location and route progress — last sent ${secsSinceSend}s ago.`;
  } else {
    title = 'Live Tracking Idle';
    subtitle = lastSentAt
      ? `No location sent in over ${Math.round(STALE_AFTER_MS / 1000)}s. Keep this tab open and moving to resume.`
      : 'GPS acquired, waiting for the first location to be sent.';
  }

  return (
    <div className={`${styles.banner} ${isLive ? '' : styles.bannerInactive}`}>
      <div className={styles.left}>
        <span className={`${styles.iconCircle} ${isLive ? '' : styles.iconCircleInactive}`}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
        </span>
        <div>
          <span className={`${styles.title} ${isLive ? '' : styles.titleInactive}`}>
            {title} {isLive && <span className={styles.alwaysOn}>Live</span>}
          </span>
          <p className={`${styles.subtitle} ${isLive ? '' : styles.subtitleInactive}`}>{subtitle}</p>
        </div>
      </div>
      <span className={isLive ? styles.broadcasting : styles.notBroadcasting}>
        <span className={isLive ? styles.dot : styles.dotOff} /> {isLive ? 'Broadcasting' : 'Not Broadcasting'}
      </span>
    </div>
  );
};
